import type { BlogPost } from '../types';

export const post: BlogPost = {
  "id": "201",
  "slug": "how-to-run-geography-quiz-corporate-seminar",
  "title": "How to Run a Geography Quiz at Your Next Corporate Seminar",
  "excerpt": "A practical guide for event organizers who want to add a geography quiz to their next conference or seminar — from choosing the right format to running it smoothly on the day.",
  "date": "2026-03-05",
  "category": "Team Building",
  "image": "https://images.unsplash.com/photo-1505373877841-8d25f7d46678?w=800&q=80",
  "readTime": "3 min",
  "content": [
    {
      "type": "paragraph",
      "text": "A geography round gives a group a shared image to discuss. Experience with maps and travel can create an advantage, so use a practice round, mixed-experience teams, and time to explain guesses. Decide whether the activity is a competition or a conversation before choosing the scoring rules."
    },
    {
      "type": "paragraph",
      "text": "This guide walks through exactly how to plan and run a geography quiz at a corporate seminar — the format decisions, the tech setup, the facilitation tips, and the common mistakes to avoid."
    },
    {
      "type": "heading",
      "text": "Step 1: Decide on Format Before Anything Else"
    },
    {
      "type": "paragraph",
      "text": "There are three main formats for a corporate geography quiz: individual competition, team-based competition, and cooperative challenge. Each has a different social dynamic. Individual competition produces clear winners and can feel isolating for people who aren't geography-confident. Team play is an option when you want discussion before a guess. Individual play gives each participant a separate answer; choose according to the session’s purpose. Cooperative challenges, where the whole room works together against a target score, work well for groups that don't respond well to competition at all."
    },
    {
      "type": "heading",
      "text": "Step 2: Choose Your Technology"
    },
    {
      "type": "paragraph",
      "text": "EarthGuessr can supply a satellite-location round in a browser. Multiplayer requires sign-in, and the lobby has a player limit. For a seminar audience, project the image and collect team answers separately. Rehearse on the venue network before the event."
    },
    {
      "type": "heading",
      "text": "Step 3: Structure the Round"
    },
    {
      "type": "list",
      "items": [
        "Keep it short: 10-15 rounds is the sweet spot for a seminar activity. Any longer and energy drops.",
        "Start easy: Lead with recognizable landmarks or distinctive geography (Norwegian fjords, Egyptian pyramids from above) to build confidence.",
        "Escalate difficulty: Middle rounds should require genuine geographic knowledge. Final rounds can be genuinely hard.",
        "Include at least one 'work relevant' location: If your company operates in specific regions, include a satellite image of one of your key sites or client regions.",
        "Allow team discussion time: Give teams 60-90 seconds per image to discuss before locking in an answer."
      ]
    },
    {
      "type": "heading",
      "text": "Step 4: Prepare Your Facilitation Script"
    },
    {
      "type": "paragraph",
      "text": "The facilitator's job is to keep energy up and make the reveal of each answer feel satisfying. For each location, prepare two or three interesting facts beyond just naming the place. 'That's the Atacama Desert in Chile — the driest non-polar desert on Earth, where NASA tests Mars rovers because the conditions are so similar.' That kind of context transforms a simple right-or-wrong quiz into something genuinely educational."
    },
    {
      "type": "heading",
      "text": "Step 5: Handle Scoring Transparently"
    },
    {
      "type": "paragraph",
      "text": "Use a shared leaderboard visible to the whole room. Even if you're using EarthGuessr's built-in multiplayer scoring, consider having a human score tracker on a separate screen for dramatic effect during the reveal. Nothing kills the energy of a quiz faster than opaque scoring that participants don't understand or trust."
    },
    {
      "type": "heading",
      "text": "Common Mistakes to Avoid"
    },
    {
      "type": "list",
      "items": [
        "Running it too late in the day when energy is low — schedule geography quizzes right after lunch, not at 4:30 PM",
        "Team size: start with small groups and check that each person has a chance to contribute",
        "Skipping the WiFi test — satellite imagery requires bandwidth; always test before the event",
        "Forgetting to brief the facilitator — the reveals are where the energy lives, so preparation matters",
        "Using obscure locations that produce no discussion — difficulty should create debate, not silence"
      ]
    }
  ]
};
